import { useState } from 'react';
import { useAppStore } from '@/store/AppStore';
import Badge, { statusTone } from '@/components/base/Badge';
import JobDetail from '@/components/feature/JobDetail';
import EmptyState from '@/components/base/EmptyState';
import type { Job } from '@/types';

const ACTIVE_STATUSES = [
  'Planlandı',
  'Hazırlanıyor',
  'Yola Çıkıldı',
  'Adrese Varıldı',
  'İş Başladı',
  'İş Devam Ediyor',
  'İş Tamamlandı',
  'Müşteri Kontrolü',
];

function elapsedLabel(job: Job): string {
  const startedEntry = job.timeline.find((t) => t.text === 'İş başladı');
  const start = startedEntry?.time ?? job.time;
  const [h, m] = start.split(':').map(Number);
  const nowMin = 10 * 60 + 15; // demo "şimdi" 10:15
  const diff = Math.max(0, nowMin - (h * 60 + m));
  const hh = Math.floor(diff / 60);
  const mm = diff % 60;
  if (hh > 0) return `${hh}s ${mm}dk`;
  return `${mm}dk`;
}

export default function AktifIsler() {
  const { jobs } = useAppStore();
  const [selected, setSelected] = useState<Job | null>(null);

  const activeJobs = jobs
    .filter((j) => ACTIVE_STATUSES.includes(j.status))
    .sort((a, b) => a.time.localeCompare(b.time));

  return (
    <div className="mx-auto max-w-[1400px] space-y-6">
      <div>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent-600">
          <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
          Operasyon
        </div>
        <h1 className="mt-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Aktif İşler</h1>
        <p className="mt-1 text-sm text-foreground-500">Şu an sahada devam eden veya bugün başlayacak işler.</p>
      </div>

      {activeJobs.length === 0 ? (
        <div className="rounded-lg border border-background-200 bg-white">
          <EmptyState
            icon="ri-tools-line"
            title="Şu an aktif iş bulunmuyor."
            description="Sahada devam eden veya bugün başlayacak bir iş yok."
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {activeJobs.map((job) => {
            const inProgress = job.status === 'İş Devam Ediyor' || job.status === 'İş Başladı';
            return (
              <button
                key={job.id}
                type="button"
                onClick={() => setSelected(job)}
                className="rounded-lg border border-background-200 bg-white p-5 text-left transition-colors hover:border-primary-200 hover:bg-background-50"
              >
                <div className="flex items-start justify-between">
                  <div className="font-heading text-lg font-bold text-foreground-950">{job.time}</div>
                  <Badge tone={statusTone(job.status)}>{job.status}</Badge>
                </div>
                <div className="mt-2 font-heading text-base font-bold text-foreground-900">{job.customerName}</div>
                <div className="text-sm text-foreground-500">{job.service}</div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-map-pin-line" /> {job.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-group-line" /> {job.team}
                  </span>
                </div>
                {inProgress && (
                  <div className="mt-3 flex items-center gap-2 rounded-md bg-accent-100/70 px-3 py-2 text-xs font-semibold text-accent-800">
                    <i className="ri-time-line" />
                    Geçen süre: {elapsedLabel(job)}
                  </div>
                )}
                <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-primary-600">
                  İşi Aç
                  <i className="ri-arrow-right-s-line" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected && <JobDetail job={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}